import { Tooltip } from "@mui/material";
import { GridSortItem } from "@mui/x-data-grid";
import { useEffect, useMemo, useState } from "react";
// useNavigate, useSearchParams, PROVIDER_PATHS kept for future use — was used by old action menu items
import CustomDataGridV2 from "../../../../../components/CustomDataGridV2";
import LoadingOverlay from "../../../../../components/LoadingOverlay";
import RenderWithTooltip from "../../../../../components/RenderWithTooltip";
import StatusChip from "../../../../../components/StatusChip";
import Text from "../../../../../components/Text";
import ActionMenu from "../../../../../components/app/ActionMenu";
import { useRoleController } from "../../../../../components/useRoleController";
// import { PROVIDER_PATHS } from "../../../../../constants/paths";
import EobDocumentPreview from "./EobDocumentPreview";
import useDisclosure from "../../../../../hooks/use-disclosure";
import { useGetEobs } from "../../../../../queries/billings/eob/use-get-eobs";
import { formatDate } from "../../../../../utils/date-time-utility";
import {
  DEF_PAGINATION_SMALL,
  DGT_Pagination,
  applyDefaultColumnSettings,
  getTrimmedUuid,
  replaceUnderScoreAddSpace,
  roleFilter,
} from "../../../../../utils/utils";
import PostPaymentDrawer from "./PostPaymentDrawer";
import UploadEobModal from "./UploadEobModal";
import UploadEraEobModal from "./UploadEraEobModal";
import ViewEobDetails from "./ViewEobDetails";
import ViewEraEob from "./ViewEraEob";
import { useOutletContext } from "react-router-dom";
import { EraEobChildContext } from "./EraEobTab";
import { EOB_STATUS_VARIANT, getEobRemainingPayment } from "./constants";

type EobDto = {
  uuid?: string;
  insurancePayer?: {
    payerId?: string;
    payerName?: string;
  };
  eobNumber?: string;
  statementDate?: string;
  paymentMethod?: string;
  totalPaymentAmount?: string | number;
  checkEftNumber?: string;
  documentFormat?: string;
  documentName?: string;
  document?: string;
  finalPayment?: string | number | null;
  remainingPayment?: string | number | null;
  status?: string | null;
  postedClaims?: Record<string, string> | string | null;
  active?: boolean;
  archive?: boolean;
  created?: string;
  uploadedBy?: string;
};

type EobWorklistRow = {
  uuid: string;
  eobAddedDate: string;
  payerName: string;
  payerId: string;
  eobNumber: string;
  docName: string;
  document: string;
  documentFormat: string;
  paymentMethod: string;
  checkEftNumber: string;
  paymentAmount: number;
  statementDate: string;
  totalPaymentAmount: string;
  mappedClaims: Record<string, string>;
  finalPayment: number;
  uploadedBy: string;
  status: string;
};

const EobWorklist = () => {
  const { filter, debouncedSearchKey, onSummaryCountsChange } =
    useOutletContext<EraEobChildContext>();
  // const navigate = useNavigate();
  const [gridSorting, setGridSorting] = useState<GridSortItem[]>([]);
  const [pagination, setPagination] =
    useState<DGT_Pagination>(DEF_PAGINATION_SMALL);
  const [downloadEobUuid, setDownloadEobUuid] = useState<string>("");

  void filter;

  const { hasView: hasViewEraEob } = useRoleController("Generate Claims");
  const { hasUpdate: hasUpdateEraEob } = useRoleController("Generate Claims");
  const { hasUpdate: hasPostPayment } = useRoleController("Generate Claims");
  // Kept for future use — was used by old action menu items
  // const { hasUpdate: hasWriteOffPayment } = useRoleController("Generate Claims");
  // const { hasUpdate: hasVoidPayment } = useRoleController("Generate Claims");

  const viewEobDisclosure = useDisclosure();
  const viewDocumentDisclosure = useDisclosure();
  const editEobDisclosure = useDisclosure();
  const postPaymentDisclosure = useDisclosure();
  const uploadEobDisclosure = useDisclosure();

  const {
    data: eobData,
    isLoading,
    isFetching,
  } = useGetEobs({
    pagination,
    gridSorting,
    searchKey: debouncedSearchKey,
  });

  const content = (eobData?.data?.content ?? []) as EobDto[];

  const formatCurrency = (amount: number) => {
    return `$${Number(amount).toFixed(2)}`;
  };

  const rows = useMemo<EobWorklistRow[]>(() => {
    return content.map((eob) => {
      const totalPayment = Number(eob?.totalPaymentAmount) || 0;
      const remaining = getEobRemainingPayment(eob);

      return {
        uuid: eob?.uuid ?? "",
        eobAddedDate: eob?.created ?? "",
        payerName: eob?.insurancePayer?.payerName ?? "-",
        payerId: eob?.insurancePayer?.payerId ?? "",
        eobNumber: eob?.eobNumber ?? "-",
        docName: eob?.documentName ?? "-",
        document: eob?.document ?? "",
        documentFormat: eob?.documentFormat ?? "",
        paymentMethod: eob?.paymentMethod ?? "-",
        checkEftNumber: eob?.checkEftNumber ?? "-",
        paymentAmount: totalPayment,
        statementDate: eob?.statementDate ?? "",
        totalPaymentAmount: String(eob?.totalPaymentAmount ?? ""),
        mappedClaims:
          typeof eob?.postedClaims === "object" && eob?.postedClaims
            ? eob.postedClaims
            : {},
        finalPayment: remaining,
        status: eob?.status ?? "",
        uploadedBy: eob?.uploadedBy ?? "-",
      };
    });
  }, [content]);

  // Update summary counts for parent
  useEffect(() => {
    const totalEobs =
      Number(eobData?.data?.page?.totalElements ?? content.length) || 0;
    onSummaryCountsChange({
      numberOfERAs: 0,
      numberOfEOBs: totalEobs,
      numberOfMappedEOBs: content.filter((eob) => eob?.status === "POSTED")
        .length,
      numberOfUnmappedEOBs: content.filter((eob) => eob?.status !== "POSTED")
        .length,
      numberOfSuccessfulPayments: content.filter(
        (eob) => eob?.status === "POSTED"
      ).length,
      numberOfPaymentFailures: content.filter(
        (eob) => eob?.status === "FULL" || !eob?.status
      ).length,
    });
  }, [content, eobData?.data?.page?.totalElements]);

  const COLUMNS = applyDefaultColumnSettings<EobWorklistRow>([
    {
      field: "eobAddedDate",
      flex: 0.6,
      minWidth: 140,
      headerName: "EOB Added Date",
      sortable: false,
      renderCell: ({ row }: { row: EobWorklistRow }) => (
        <Text isTabletext>{formatDate(row?.eobAddedDate) ?? "-"}</Text>
      ),
    },
    {
      field: "payerName",
      flex: 0.7,
      minWidth: 150,
      headerName: "Payer Name",
      sortable: false,
      renderCell: ({ row }: { row: EobWorklistRow }) => {
        const name = row?.payerName || "-";
        return (
          <Tooltip title={name?.length > 30 ? name : ""} placement="top" arrow>
            <span>
              <Text isTabletext>
                {name?.length > 30 ? `${name.slice(0, 30)}...` : name}
              </Text>
            </span>
          </Tooltip>
        );
      },
    },
    {
      field: "eobNumber",
      flex: 0.5,
      minWidth: 100,
      headerName: "EOB #",
      sortable: false,
      renderCell: ({ row }: { row: EobWorklistRow }) => (
        <Text isTabletext>{row?.eobNumber || "-"}</Text>
      ),
    },
    {
      field: "docName",
      flex: 0.6,
      minWidth: 130,
      headerName: "Doc. Name",
      sortable: false,
      renderCell: ({ row }: { row: EobWorklistRow }) => {
        const name = row?.docName || "-";
        if (!row?.document || name === "-") {
          return <RenderWithTooltip value={name} />;
        }
        const truncated = name.length > 25 ? `${name.slice(0, 25)}...` : name;
        return (
          <Tooltip title={name} placement="top" arrow>
            <span>
              <Text
                isTabletext
                onClick={() => viewDocumentDisclosure.open(row.uuid)}
                sx={{
                  color: "#2C57B3",
                  cursor: "pointer",
                  fontWeight: 500,
                  textDecoration: "underline",
                  "&:hover": { textDecoration: "underline" },
                }}
              >
                {truncated}
              </Text>
            </span>
          </Tooltip>
        );
      },
    },
    {
      field: "paymentMethod",
      flex: 0.6,
      minWidth: 130,
      headerName: "Payment Method",
      sortable: false,
      renderCell: ({ row }: { row: EobWorklistRow }) => (
        <Text isTabletext>
          {replaceUnderScoreAddSpace(row?.paymentMethod) || "-"}
        </Text>
      ),
    },
    {
      field: "checkEftNumber",
      flex: 0.6,
      minWidth: 130,
      headerName: "Cheque/EFT #",
      sortable: false,
      renderCell: ({ row }: { row: EobWorklistRow }) => (
        <Text isTabletext>{row?.checkEftNumber || "-"}</Text>
      ),
    },
    {
      field: "paymentAmount",
      flex: 0.6,
      minWidth: 140,
      headerName: "Payment Amount",
      sortable: false,
      renderCell: ({ row }: { row: EobWorklistRow }) => (
        <Text isTabletext>{formatCurrency(row?.paymentAmount)}</Text>
      ),
    },
    {
      field: "mappedClaims",
      flex: 0.6,
      minWidth: 130,
      headerName: "Posted Claims",
      sortable: false,
      renderCell: ({ row }: { row: EobWorklistRow }) => {
        const claims = Object.values(row?.mappedClaims || {});
        if (claims.length === 0) return <Text isTabletext>-</Text>;
        const firstClaim = getTrimmedUuid(claims[0]);
        const remaining = claims.length - 1;
        return (
          <Tooltip
            title={claims.map((c) => getTrimmedUuid(c)).join(", ")}
            placement="top"
            arrow
          >
            <span>
              <Text isTabletext>
                {firstClaim}
                {remaining > 0 ? ` +${remaining}` : ""}
              </Text>
            </span>
          </Tooltip>
        );
      },
    },
    {
      field: "remainingPayment",
      flex: 0.6,
      minWidth: 130,
      headerName: "Rem. Payment",
      sortable: false,
      renderCell: ({ row }: { row: EobWorklistRow }) => (
        <Text isTabletext>{formatCurrency(row?.finalPayment)}</Text>
      ),
    },
    {
      field: "status",
      flex: 0.5,
      minWidth: 100,
      headerName: "Status",
      sortable: false,
      renderCell: ({ row }: { row: EobWorklistRow }) => {
        const status = row?.status?.toUpperCase() || "PENDING";
        // Backend statuses are SCREAMING_SNAKE (PARTIALLY_POSTED); show them
        // as words — lowercase first, since the helper only uppercases each
        // word's initial and would otherwise leave the value in all caps.
        const label = replaceUnderScoreAddSpace(status.toLowerCase());
        return (
          // The longer labels ("Partially Posted") do not fit the column, so
          // the full status is available on hover.
          <Tooltip title={label} placement="top" arrow>
            <span style={{ display: "inline-flex", minWidth: 0 }}>
              <StatusChip
                label={label}
                variant={EOB_STATUS_VARIANT[status] ?? "default"}
              />
            </span>
          </Tooltip>
        );
      },
    },
    {
      field: "uploadedBy",
      flex: 0.6,
      minWidth: 140,
      headerName: "Uploaded By",
      sortable: false,
      renderCell: ({ row }: { row: EobWorklistRow }) => (
        <Text isTabletext>{row?.uploadedBy || "-"}</Text>
      ),
    },
    {
      field: "Action",
      width: 90,
      hideSortIcons: true,
      renderCell: ({ row }: { row: EobWorklistRow }) => {
        return (
          <ActionMenu
            options={roleFilter({
              list: [
                { value: "view_eob_details", label: "View EOB Details" },
                { value: "view_document", label: "View Document" },
                {
                  value: "edit_eob",
                  label: "Edit EOB",
                  isDisabled: row.status === "POSTED",
                },
                {
                  value: "post_payment",
                  label: "Post Payment",
                  isDisabled: row.status === "POSTED",
                },
              ],
              compWith: {
                view_eob_details: hasViewEraEob,
                view_document: hasViewEraEob,
                edit_eob: hasUpdateEraEob,
                post_payment: hasPostPayment,
              },
            })}
            onActionClick={(v: string) => {
              if (v === "view_eob_details") {
                viewEobDisclosure.open(row.uuid);
              } else if (v === "view_document") {
                viewDocumentDisclosure.open(row.uuid);
              } else if (v === "edit_eob") {
                editEobDisclosure.open({ uuid: row.uuid });
              } else if (v === "post_payment") {
                postPaymentDisclosure.open({
                  uuid: row.uuid,
                  payerId: row.payerId,
                });
              }
            }}
          />
        );
      },
    },
  ]);

  return (
    <>
      <LoadingOverlay isLoading={!!downloadEobUuid} />

      <UploadEraEobModal disclosureHost={uploadEobDisclosure} />

      <UploadEobModal disclosureHost={editEobDisclosure} />

      <EobDocumentPreview disclosureHost={viewDocumentDisclosure} />

      {postPaymentDisclosure.opened && (
        <PostPaymentDrawer disclosureHost={postPaymentDisclosure} />
      )}

      <ViewEobDetails
        disclosureHost={viewEobDisclosure}
        onPreviewDocument={(uuid: string) => {
          viewEobDisclosure.close();
          viewDocumentDisclosure.open(uuid);
        }}
      />
      <ViewEraEob
        disclosureHost={{
          opened: false,
          open: () => undefined,
          close: () => undefined,
          data: undefined,
        }}
        eraEobUuid={
          downloadEobUuid
            ? { uuid: downloadEobUuid, autoDownload: true }
            : undefined
        }
        onAutoDownloadComplete={() => setDownloadEobUuid("")}
      />

      <CustomDataGridV2
        searchString={debouncedSearchKey}
        loading={isLoading || isFetching}
        rows={rows}
        getRowId={(row: EobWorklistRow) => row?.uuid}
        columns={COLUMNS}
        onPaginationModelChange={setPagination}
        onSortModelChange={setGridSorting}
        paginationModel={pagination}
        rowCount={eobData?.data?.page?.totalElements ?? rows.length}
        sortModel={gridSorting}
      />
    </>
  );
};

export default EobWorklist;
